import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaBriefcase,
  FaCheckCircle,
  FaEdit,
  FaExternalLinkAlt,
  FaHome,
  FaHotel,
  FaMapMarkedAlt,
  FaMapMarkerAlt,
  FaPlus,
  FaRegCircle,
  FaTimes,
  FaTrashAlt,
} from 'react-icons/fa';
import { API_BASE_URL } from '../../../config/api';
import {
  buildAuthRequiredRedirectUrl,
  clearStoredAuthIfUnauthorized,
  extractApiErrorMessage,
  parseResponseJson,
  readStoredAuth,
  SIGN_IN_REQUIRED_MESSAGE,
} from '../profileFormUtils';
import {
  canAddSavedAddress,
  chooseCanonicalPrimaryId,
  demoteExtraPrimaryAddresses,
  MAX_SAVED_ADDRESSES,
  SAVED_ADDRESS_LIMIT_MESSAGE,
} from './addressManagementUtils';
import {
  clearSelectedAddressId,
  getStoredSelectedAddressId,
  storeSelectedAddressId,
} from '../../Cart/cartDistanceUtils';
import AddressFormModal from './AddressFormModal';
import { normalizeAddressFromRaw } from './addressFormModalUtils';
import './AccountAddresses.css';

const ADDRESS_TYPES = [
  { value: 0, label: 'Home', icon: FaHome },
  { value: 1, label: 'Work', icon: FaBriefcase },
  { value: 2, label: 'Hotel', icon: FaHotel },
  { value: 3, label: 'Other', icon: FaMapMarkerAlt },
];

function addressTypeMeta(type) {
  return ADDRESS_TYPES.find((item) => item.value === Number(type)) || ADDRESS_TYPES[3];
}

const AccountAddresses = () => {
  const [addresses, setAddresses] = useState([]);
  const [selectedId, setSelectedId] = useState(() => getStoredSelectedAddressId());
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [mapPreviewAddress, setMapPreviewAddress] = useState(null);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);

  const auth = useMemo(() => readStoredAuth(), []);

  const authHeaders = useCallback(() => {
    const token = readStoredAuth().token;
    if (!token) throw new Error(SIGN_IN_REQUIRED_MESSAGE);
    return {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }, []);

  const requestAddressApi = useCallback(
    async (path, { method = 'GET', body } = {}) => {
      const headers = authHeaders();
      const res = await fetch(`${API_BASE_URL}${path}`, {
        method,
        headers: {
          ...headers,
          ...(body ? { 'Content-Type': 'application/json' } : null),
        },
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await parseResponseJson(res);
      if (!res.ok) {
        clearStoredAuthIfUnauthorized(res.status);
        const fromBody = extractApiErrorMessage(data);
        throw new Error(fromBody || (res.status === 401 ? SIGN_IN_REQUIRED_MESSAGE : 'Address request failed.'));
      }
      return data;
    },
    [authHeaders],
  );

  const loadAddresses = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await requestAddressApi('/auth/getUserAddress');
      let list = Array.isArray(data?.data) ? data.data.map(normalizeAddressFromRaw) : [];
      const storedId = getStoredSelectedAddressId();
      const selectedExists = storedId != null && list.some((item) => item.id === storedId);
      let nextSelected = selectedExists ? storedId : null;

      const primaryCount = list.filter((item) => item.isPrimary).length;
      if (primaryCount > 1) {
        const keepPrimaryId = chooseCanonicalPrimaryId(list, { selectedId: nextSelected });
        if (keepPrimaryId != null) {
          const demoted = await demoteExtraPrimaryAddresses(requestAddressApi, list, keepPrimaryId);
          if (demoted > 0) {
            const refreshed = await requestAddressApi('/auth/getUserAddress');
            list = Array.isArray(refreshed?.data) ? refreshed.data.map(normalizeAddressFromRaw) : list;
            setMessage('Only one default address is allowed. Extra default flags were cleared.');
          }
        }
      }

      setAddresses(list);
      setSelectedId(nextSelected);
      if (selectedExists) storeSelectedAddressId(nextSelected);
      else clearSelectedAddressId();
    } catch (err) {
      setError(err?.message || 'Could not load saved addresses.');
    } finally {
      setLoading(false);
    }
  }, [requestAddressApi]);

  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  const sortedAddresses = useMemo(() => [...addresses], [addresses]);

  const openNewAddressModal = () => {
    if (!canAddSavedAddress(addresses.length)) {
      setMessage('');
      setError(SAVED_ADDRESS_LIMIT_MESSAGE);
      return;
    }
    setError('');
    setEditingAddress(null);
    setAddressModalOpen(true);
  };

  const editAddress = (address) => {
    setEditingAddress(address);
    setAddressModalOpen(true);
  };

  const closeAddressModal = () => {
    setAddressModalOpen(false);
    setEditingAddress(null);
  };

  const handleAddressSaved = async (saved) => {
    const wasEditing = editingAddress?.id != null;
    setAddressModalOpen(false);
    setEditingAddress(null);
    await loadAddresses();
    setMessage(wasEditing ? 'Address updated successfully.' : 'Address saved successfully.');
    if (!wasEditing && saved?.id != null) {
      setSelectedId(saved.id);
      storeSelectedAddressId(saved.id);
    }
  };

  const selectAddress = (id) => {
    setSelectedId(id);
    storeSelectedAddressId(id);
    const selected = addresses.find((item) => item.id === id);
    setMessage(selected?.name ? `Delivering to ${selected.name}.` : '');
  };

  const deleteAddress = async (address) => {
    const confirmed = window.confirm(`Remove ${address.name || 'this address'}?`);
    if (!confirmed) return;
    setDeletingId(address.id);
    setError('');
    setMessage('');
    try {
      await requestAddressApi(`/auth/deleteUserAddress/${encodeURIComponent(address.id)}`);
      if (selectedId === address.id) clearSelectedAddressId();
      await loadAddresses();
      setMessage('Address removed.');
    } catch (err) {
      setError(err?.message || 'Could not remove address.');
    } finally {
      setDeletingId(null);
    }
  };

  const mapPreviewCoords =
    mapPreviewAddress &&
    Number.isFinite(mapPreviewAddress.latitude) &&
    Number.isFinite(mapPreviewAddress.longitude)
      ? `${mapPreviewAddress.latitude.toFixed(6)}, ${mapPreviewAddress.longitude.toFixed(6)}`
      : '';
  const mapPreviewUrl = mapPreviewCoords
    ? `https://www.google.com/maps?q=${encodeURIComponent(mapPreviewCoords)}`
    : '';

  if (!auth.token) {
    window.location.assign(buildAuthRequiredRedirectUrl('/account/addresses'));
    return null;
  }

  return (
    <div className="account-page account-addresses">
      <div className="account-addresses__header">
        <div>
          <h1>Delivery Addresses</h1>
          <p className="account-page__muted">
            Choose a delivery address with the circle control. Only one address can be the default.
            Cart pricing uses the address you select, not every default flag.
          </p>
        </div>
        <button type="button" className="account-addresses__add-shortcut" onClick={openNewAddressModal}>
          <FaPlus aria-hidden />
          Add new address
        </button>
      </div>

      {message ? <div className="auth-inline-message auth-inline-message--success">{message}</div> : null}
      {error ? (
        <div
          className="auth-inline-message auth-inline-message--error account-addresses__alert"
          role="alert"
        >
          <span>{error}</span>
          <button
            type="button"
            className="account-addresses__alert-close"
            onClick={() => setError('')}
            aria-label="Dismiss message"
          >
            <FaTimes aria-hidden />
          </button>
        </div>
      ) : null}

      <AddressFormModal
        open={addressModalOpen}
        onClose={closeAddressModal}
        onSaved={handleAddressSaved}
        editAddress={editingAddress}
        addressesCount={addresses.length}
        maxAddresses={MAX_SAVED_ADDRESSES}
      />

      <div className="account-addresses__workspace">
        <section className="account-addresses__saved" aria-label="Saved addresses">
          <div className="account-addresses__saved-head">
            <h2>Saved addresses</h2>
          </div>

          {!loading && addresses.length === 0 ? (
            <div className="account-addresses__select-banner" role="status">
              Add your first delivery address below so delivery and tax can be calculated on your
              cart.
            </div>
          ) : null}
          {!loading && addresses.length > 0 && selectedId == null ? (
            <div className="account-addresses__select-banner" role="status">
              Select an address with the circle control so delivery and tax can be calculated on
              your cart.
            </div>
          ) : null}
          {!loading && !canAddSavedAddress(addresses.length) ? (
            <div className="account-addresses__select-banner account-addresses__select-banner--limit" role="status">
              You have reached the limit of {MAX_SAVED_ADDRESSES} saved addresses. Delete one to add
              a new address.
            </div>
          ) : null}

          {loading ? (
            <div className="account-addresses__empty">Loading saved addresses...</div>
          ) : sortedAddresses.length ? (
            <div className="account-addresses__cards">
              {sortedAddresses.map((address) => {
                const meta = addressTypeMeta(address.type);
                const Icon = meta.icon;
                const selected = address.id === selectedId;
                return (
                  <article
                    key={address.id}
                    className={`address-card${selected ? ' address-card--selected' : ''}`}
                  >
                    <button
                      type="button"
                      className="address-card__select"
                      onClick={() => selectAddress(address.id)}
                      aria-label={`Deliver to ${address.name}`}
                    >
                      {selected ? <FaCheckCircle aria-hidden /> : <FaRegCircle aria-hidden />}
                    </button>
                    <div className="address-card__icon">
                      <Icon aria-hidden />
                    </div>
                    <div className="address-card__body">
                      <div className="address-card__title-row">
                        <h3>{address.name || meta.label}</h3>
                        <div className="address-card__badges">
                          {selected ? (
                            <span className="address-card__badge--delivery">Delivering here</span>
                          ) : null}
                          {address.isPrimary ? <span>Default</span> : null}
                          <span>{meta.label}</span>
                        </div>
                      </div>
                      <p>
                        {[address.address, address.city, address.state, address.postalCode]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                      {Number.isFinite(address.latitude) && Number.isFinite(address.longitude) ? (
                        <div className="address-card__location-meta">
                          <button type="button" onClick={() => setMapPreviewAddress(address)}>
                            <FaMapMarkedAlt aria-hidden />
                            View map
                          </button>
                        </div>
                      ) : null}
                    </div>
                    <div className="address-card__actions">
                      <button type="button" onClick={() => editAddress(address)} aria-label="Edit address">
                        <FaEdit aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteAddress(address)}
                        disabled={deletingId === address.id}
                        aria-label="Remove address"
                      >
                        <FaTrashAlt aria-hidden />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="account-addresses__empty">
              <FaMapMarkerAlt aria-hidden />
              <strong>No saved addresses yet.</strong>
              <span>Add a delivery address to make checkout faster.</span>
              <button type="button" className="account-addresses__empty-action" onClick={openNewAddressModal}>
                <FaPlus aria-hidden />
                Add new address
              </button>
            </div>
          )}
        </section>
      </div>

      {mapPreviewAddress ? (
        <div
          className="account-addresses__map-preview-modal"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setMapPreviewAddress(null);
          }}
        >
          <section
            className="account-addresses__map-preview"
            role="dialog"
            aria-modal="true"
            aria-label="Saved address map preview"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="account-addresses__map-preview-head">
              <div>
                <h2>{mapPreviewAddress.name || 'Saved address'}</h2>
                <span>{mapPreviewCoords}</span>
              </div>
              <button type="button" onClick={() => setMapPreviewAddress(null)}>
                Close
              </button>
            </div>
            <iframe
              className="account-addresses__map-preview-frame"
              title={`${mapPreviewAddress.name || 'Saved address'} map`}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src={`${mapPreviewUrl}&z=16&output=embed`}
            />
            <div className="account-addresses__map-preview-actions">
              <a href={mapPreviewUrl} target="_blank" rel="noopener noreferrer">
                <FaExternalLinkAlt aria-hidden />
                Open in Google Maps
              </a>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
};

export default AccountAddresses;
